import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, CheckCircle2 
} from 'lucide-react';

const ContactUs = () => {
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    serviceInterest: '',
    workforceSize: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    if (name === 'phone') {
      newValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({ ...prev, [name]: newValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Please fill your name";
    if (!formData.company.trim()) newErrors.company = "Please fill company name";
    
    if (!formData.email.trim()) {
      newErrors.email = "Please fill your email";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Please fill your phone number";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }

    if (!formData.serviceInterest) newErrors.serviceInterest = "Please select a service area";
    if (!formData.workforceSize) newErrors.workforceSize = "Please select workforce size";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    try {
      const response = await fetch('https://formsubmit.co/administrator@tickbox.co.in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          "Name": formData.name,
          "Company": formData.company,
          "E-mail": formData.email,
          "Phone": formData.phone,
          "Service Interest": formData.serviceInterest,
          "Workforce Size": formData.workforceSize,
          "Message": formData.message || "No message provided",

          _subject: `New Consultation Request from ${formData.name} - ${formData.company}`,
          _template: 'table',
          _captcha: 'false',
          _next: window.location.href
        })
      });

      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: "Thank you! Your consultation request has been sent successfully. We'll contact you soon."
        });
        
        setFormData({
          name: '', company: '', email: '', phone: '',
          serviceInterest: '', workforceSize: '', message: ''
        });
        setErrors({});
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: "Something went wrong. Please try again or email us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, label: "City", value: "Madurai, Tamil Nadu, India" },
    { icon: Phone, label: "Phone", value: "+91 93857 94811" },
    { icon: Mail, label: "Email (Support)", value: "administrator@tickbox.co.in" },
    { icon: Mail, label: "Email (Sales)", value: "sales@tickbox.co.in" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">

      <section className="pt-15 pb-25 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Left Column: Response Commitment + Contact Information */}
            <div className="order-2 lg:order-1 lg:col-span-6 space-y-12">
              
              {/* Our Response Commitment */}
              <div>
                <motion.h2
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-4xl font-bold tracking-tight mb-8 bg-gradient-to-r from-[#d97925] to-red-500 bg-clip-text text-transparent"
                >
                  Our Response Commitment
                </motion.h2>

                <motion.div
                  initial={{ opacity: 0, y: 80 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-white to-[#f5f5f5] rounded-3xl p-10 border border-[#d97925] shadow-lg"
                >
                  <ul className="grid md:grid-cols-2 gap-8">
                    {[
                      { time: "General enquiries", desc: "Response within 24 business hours" },
                      { time: "Consultation requests", desc: "Call scheduled within 48 hours" },
                      { time: "Support tickets", desc: "Initial response within 4 business hours" },
                      { time: "Urgent compliance queries", desc: "Priority callback within 2 hours" }
                    ].map((commit, i) => (
                      <li key={i} className="flex items-start gap-4 text-lg">
                        <CheckCircle2 className="text-[#d97925] mt-1.5 flex-shrink-0" size={26} />
                        <div>
                          <span className="font-semibold text-[#d97925]">{commit.time}:</span>
                          <span className="text-zinc-700"> {commit.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>

              {/* Contact Information - Now directly below */}
                           {/* Contact Information */}
              <div>
                <motion.h2
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-4xl font-bold tracking-tight mb-8 bg-gradient-to-r from-[#d97925] to-red-500 bg-clip-text text-transparent"
                >
                  Contact Information
                </motion.h2>

                <div className="grid md:grid-cols-2 gap-6">
                  {contactInfo.map((info, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 60 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      whileHover={{ y: -4, scale: 1.01 }}
                      className="group bg-white border border-zinc-100 rounded-3xl p-6 shadow-lg hover:shadow-xl hover:border-[#d97925] transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="mt-1 w-10 h-10 rounded-2xl bg-white-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-100 transition-colors mb-4">
                          <info.icon className="text-[#d97925]" size={22} />
                        </div>
                        <h4 className="text-lg font-semibold text-[#d97925] mb-1.5">{info.label}</h4>
                        <p className="text-base text-zinc-700 leading-relaxed break-words text-center">
                          {info.value}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

            </div>

            {/* Right Column: Consultation Form */}
            <div className="order-1 lg:order-2 lg:col-span-6">
              <motion.div
                initial={{ opacity: 0, x: 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white border border-zinc-200 rounded-3xl p-10 shadow-xl sticky top-8"
              >
                <h3 className="text-3xl font-bold mb-8 text-[#d97925]">Request a Free Consultation</h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Form fields remain unchanged */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-600 mb-2">YOUR NAME</label>
                      <input 
                        type="text" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        placeholder="Full name" 
                        className={`w-full px-5 py-3.5 border rounded-2xl focus:outline-none focus:border-[#d97925] ${errors.name ? 'border-red-500' : 'border-zinc-300'}`} 
                      />
                      {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-600 mb-2">COMPANY</label>
                      <input 
                        type="text" 
                        name="company" 
                        value={formData.company} 
                        onChange={handleInputChange} 
                        placeholder="Company name" 
                        className={`w-full px-5 py-3.5 border rounded-2xl focus:outline-none focus:border-[#d97925] ${errors.company ? 'border-red-500' : 'border-zinc-300'}`} 
                      />
                      {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-600 mb-2">EMAIL</label>
                      <input 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        placeholder="your@company.com" 
                        className={`w-full px-5 py-3.5 border rounded-2xl focus:outline-none focus:border-[#d97925] ${errors.email ? 'border-red-500' : 'border-zinc-300'}`} 
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-600 mb-2">PHONE</label>
                      <input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleInputChange} 
                        placeholder="XXXXXXXXXX" 
                        maxLength={10}
                        className={`w-full px-5 py-3.5 border rounded-2xl focus:outline-none focus:border-[#d97925] ${errors.phone ? 'border-red-500' : 'border-zinc-300'}`} 
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-600 mb-2">SERVICE INTEREST</label>
                    <select 
                      name="serviceInterest" 
                      value={formData.serviceInterest} 
                      onChange={handleInputChange} 
                      className={`w-full px-5 py-3.5 border rounded-2xl focus:outline-none focus:border-[#d97925] bg-white ${errors.serviceInterest ? 'border-red-500' : 'border-zinc-300'}`}
                    >
                      <option value="">Select a service area</option>
                      <option value="Establishment Compliance Services">Establishment Compliance Services</option>
                      <option value="Factory Compliance Services">Factory Compliance Services</option>
                      <option value="Contract Labour Compliance Services">Contract Labour Compliance Services</option>
                      <option value="Payroll Services">Payroll Services</option>
                      <option value="Payroll Compliance Services">Payroll Compliance Services</option>
                      <option value="Industrial Licensing Services">Industrial Licensing Services</option>
                      <option value="Compliance & Audit Consulting">Compliance & Audit Consulting</option>
                      <option value="Inspection Handling Services">Inspection Handling Services</option>
                      <option value="Compliance Legal Services">Compliance Legal Services</option>
                      <option value="Other">Need Multiple Services</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.serviceInterest && <p className="text-red-500 text-sm mt-1">{errors.serviceInterest}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-600 mb-2">WORKFORCE SIZE</label>
                    <select 
                      name="workforceSize" 
                      value={formData.workforceSize} 
                      onChange={handleInputChange} 
                      className={`w-full px-5 py-3.5 border rounded-2xl focus:outline-none focus:border-[#d97925] bg-white ${errors.workforceSize ? 'border-red-500' : 'border-zinc-300'}`}
                    >
                      <option value="">Select workforce size</option>
                      <option value="1-50">1 - 50 employees</option>
                      <option value="51-200">51 - 200 employees</option>
                      <option value="201-500">201 - 500 employees</option>
                      <option value="501-1000">501 - 1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                    {errors.workforceSize && <p className="text-red-500 text-sm mt-1">{errors.workforceSize}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-600 mb-2">MESSAGE (OPTIONAL)</label>
                    <textarea 
                      name="message" 
                      value={formData.message} 
                      onChange={handleInputChange} 
                      placeholder="Tell us about your compliance requirements..." 
                      rows={5}
                      className="w-full px-5 py-4 border border-zinc-300 rounded-3xl focus:outline-none focus:border-[#d97925] resize-y" 
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#d97925] hover:bg-[#c06a1a] disabled:bg-zinc-400 text-white font-semibold text-xl py-7 rounded-3xl transition-all shadow-lg flex items-center justify-center gap-3"
                  >
                    {isSubmitting ? "Sending..." : "Request a Free Consultation"}
                  </motion.button>
                </form>

                {submitStatus.message && (
                  <div className={`mt-6 p-4 rounded-2xl text-center text-sm font-medium ${submitStatus.success ? 'bg-[#d97925] text-white' : 'bg-red-100 text-red-700'}`}>
                    {submitStatus.message}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;